import { Controller } from "@hotwired/stimulus"
import { formatMoney } from "./utils/money"

// budget-preview — preview vivo do form de orçamento: espelha a CATEGORIA
// escolhida (ícone + nome no chip), o TETO digitado (valor formatado) e o MÊS
// (sufixo "por mês" / "só em <mês>"). Como o orçamento não tem ícone próprio, o
// chip reflete o ícone da categoria: os SVGs de cada categoria são renderizados
// server-side num container oculto (icon helper) e o preview CLONA o SVG da
// célula da categoria selecionada — nada de SVG no JS. CSP-safe.
//
// Uso: data-controller="budget-preview" com:
//   inputs: categoryInput (<select>), limitInput, monthInput (<select>)
//   saída:  chip, nameOut, subOut
//   grade:  iconCell (uma por categoria; data-category-id, contém o SVG)
//   values: nameFallback, currencySymbol, everyMonthLabel, forMonthPrefix
export default class extends Controller {
  static targets = [
    "categoryInput", "limitInput", "monthInput",
    "chip", "nameOut", "subOut", "iconCell"
  ]
  static values = {
    nameFallback:   String,  // ex.: "Orçamento"
    currencySymbol: String,  // ex.: "R$"
    currency: Object,        // meta da moeda (FormatHelper::CURRENCY_META)
    everyMonthLabel: String, // ex.: "por mês"
    forMonthPrefix:  String  // ex.: "só em " (concatenado ao label do mês)
  }

  connect() {
    this.render()
    this._renderIcon()
  }

  render() {
    // Nome = nome da categoria escolhida (do <option> selecionado) ou fallback.
    if (this.hasNameOutTarget) {
      this.nameOutTarget.textContent = this._categoryName() || this.nameFallbackValue || ""
    }
    // Sub = teto formatado + sufixo de recorrência (por mês / só em <mês>).
    if (this.hasSubOutTarget) {
      const cents = this._limitCents()
      if (cents > 0) {
        this.subOutTarget.textContent = `${this._formatMoney(cents)} · ${this._recurrenceLabel()}`
      } else {
        this.subOutTarget.textContent = this._recurrenceLabel()
      }
    }
  }

  // Chamado ao trocar a categoria: atualiza nome E ícone do chip.
  changeCategory() {
    this.render()
    this._renderIcon()
  }

  // ---- Categoria ----
  _categoryName() {
    if (!this.hasCategoryInputTarget) return ""
    const sel = this.categoryInputTarget
    const opt = sel.options[sel.selectedIndex]
    // Ignora o placeholder (value vazio).
    return sel.value && opt ? opt.textContent.trim() : ""
  }

  _renderIcon() {
    if (!this.hasChipTarget) return
    const cell = this._cellForCategory(this.hasCategoryInputTarget ? this.categoryInputTarget.value : "")
    const svg = cell && cell.querySelector("svg")
    if (svg) this.chipTarget.innerHTML = svg.outerHTML
  }

  _cellForCategory(id) {
    // Sem categoria escolhida → primeira célula (ícone genérico de fallback).
    return this.iconCellTargets.find((c) => c.dataset.categoryId === String(id)) ||
           this.iconCellTargets[0]
  }

  // ---- Teto (dinheiro) ----
  // Lê os dígitos do input (cru "121212" ou mascarado "1.212,12") como centavos.
  // À prova da ordem de init com o mask controller: sempre parte dos dígitos.
  _limitCents() {
    if (!this.hasLimitInputTarget) return 0
    const digits = (this.limitInputTarget.value || "").replace(/\D/g, "")
    return digits ? parseInt(digits, 10) : 0
  }

  // Formata pelo meta da moeda do app (mesmo algoritmo do servidor - utils/money).
  // Antes fixava pt-BR e 2 casas aqui: em JPY/CLP inventava decimais que o servidor
  // não renderiza, e os separadores saíam trocados fora de BRL.
  _formatMoney(cents) {
    return formatMoney(cents, this.hasCurrencyValue ? this.currencyValue : { symbol: this.currencySymbolValue })
  }

  // ---- Mês (recorrência) ----
  _recurrenceLabel() {
    if (!this.hasMonthInputTarget || !this.monthInputTarget.value) {
      return this.everyMonthLabelValue || ""
    }
    const sel = this.monthInputTarget
    const opt = sel.options[sel.selectedIndex]
    const monthLabel = opt ? opt.textContent.trim() : ""
    return `${this.forMonthPrefixValue || ""}${monthLabel}`
  }
}
